import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { fm } from '../../utils';
import { DollarSign, ShoppingCart, TrendingUp, Users, Coffee, Package, Wifi, BarChart3, Receipt } from 'lucide-react';
import KpiCard from './KpiCard';
import Filters from './Filters';
import SalesChart from './SalesChart';
import PaymentChart from './PaymentChart';
import { TopProducts, CategoryChart } from './TopProducts';
import { WaiterStats, TableAnalysis, Heatmap } from './WaiterStats';
import LiveWidgets from './LiveWidgets';

const API = process.env.REACT_APP_API_URL || '/api';

const toDateStr = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const getDateRange = (period) => {
  const now = new Date();
  switch (period) {
    case 'today':
      return { start: toDateStr(now), end: toDateStr(now) };
    case 'yesterday': {
      const y = new Date(now); y.setDate(y.getDate() - 1);
      return { start: toDateStr(y), end: toDateStr(y) };
    }
    case 'thisWeek': {
      const monday = new Date(now); monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
      return { start: toDateStr(monday), end: toDateStr(now) };
    }
    case 'thisMonth':
      return { start: toDateStr(new Date(now.getFullYear(), now.getMonth(), 1)), end: toDateStr(now) };
    default:
      return { start: toDateStr(now), end: toDateStr(now) };
  }
};

const ReportsDashboard = () => {
  const { token } = useAuth();
  const [activePeriod, setActivePeriod] = useState('today');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [gunlukOzet, setGunlukOzet] = useState(null);
  const [dunOzet, setDunOzet] = useState(null);
  const [gelirGider, setGelirGider] = useState([]);
  const [enCokSatilan, setEnCokSatilan] = useState([]);
  const [kategoriDagilimi, setKategoriDagilimi] = useState([]);
  const [odemeDagilimi, setOdemeDagilimi] = useState([]);
  const [garsonSatislari, setGarsonSatislari] = useState([]);
  const [masaDurumu, setMasaDurumu] = useState([]);
  const [siparisler, setSiparisler] = useState([]);
  const [aylikGelirGider, setAylikGelirGider] = useState([]);
  const [masaAnalizi, setMasaAnalizi] = useState([]);
  const intervalRef = useRef(null);

  const fetchJson = useCallback(async (url, opts) => {
    const hh = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
    try { const r = await fetch(url, opts || { headers: hh }); return r.ok ? await r.json() : null; }
    catch (e) { console.error('Fetch error:', url, e); return null; }
  }, [token]);

  const fetchAllData = useCallback(async (period, silent) => {
    const hh = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
    if (!silent) setLoading(true);
    const range = period === 'custom' ? { start: startDate, end: endDate } : getDateRange(period);
    const prevRange = getDateRange('yesterday');

    const now = new Date();
    const ayBaslangic = toDateStr(new Date(now.getFullYear(), now.getMonth(), 1));
    const bugun = toDateStr(now);

    const [ozet, ozetDun, gelir, top, kat, odeme, garson, masa, sip, aylikGelir, masaAnaliz] = await Promise.all([
      fetchJson(`${API}/raporlar/gunluk-ozet?tarih=${range.start}`),
      fetchJson(`${API}/raporlar/gunluk-ozet?tarih=${prevRange.start}`),
      fetchJson(`${API}/raporlar/gelir-gider?baslangic=${range.start}&bitis=${range.end}`),
      fetchJson(`${API}/raporlar/en-cok-satilan?limit=15&baslangic=${range.start}&bitis=${range.end}`),
      fetchJson(`${API}/raporlar/kategori-dagilimi?baslangic=${range.start}&bitis=${range.end}`),
      fetchJson(`${API}/raporlar/odeme-dagilimi?baslangic=${range.start}&bitis=${range.end}`),
      fetchJson(`${API}/raporlar/garson-satislari?baslangic=${range.start}&bitis=${range.end}`),
      fetchJson(`${API}/raporlar/masa-durumu`),
      fetchJson(`${API}/siparisler`, { headers: hh }),
      fetchJson(`${API}/raporlar/gelir-gider?baslangic=${ayBaslangic}&bitis=${bugun}`),
      fetchJson(`${API}/raporlar/masa-analizi?baslangic=${range.start}&bitis=${range.end}`),
    ]);

    setGunlukOzet(ozet);
    setDunOzet(ozetDun);
    setGelirGider(gelir?.gunluk || []);
    setEnCokSatilan(top || []);
    setKategoriDagilimi(kat || []);
    setOdemeDagilimi((odeme || []).map(o => ({ name: o.odemeTuru, value: o.toplamTutar, yuzde: o.yuzde, adet: o.adet })));
    setGarsonSatislari(garson || []);
    setMasaDurumu(masa || []);
    setSiparisler(sip || []);
    setAylikGelirGider(aylikGelir?.gunluk || []);
    setMasaAnalizi(masaAnaliz || []);
    if (!silent) setLoading(false);
  }, [fetchJson, startDate, endDate, token]);

  useEffect(() => {
    if (activePeriod === 'custom' && (!startDate || !endDate)) return;
    fetchAllData(activePeriod);
    intervalRef.current = setInterval(() => fetchAllData(activePeriod, true), 30000);
    return () => clearInterval(intervalRef.current);
  }, [activePeriod, fetchAllData, startDate, endDate]);

  const handlePeriodChange = (p) => {
    setActivePeriod(p);
    if (p !== 'custom') fetchAllData(p);
  };

  const toplamCiro = gelirGider.reduce((t, d) => t + (d.toplam || 0), 0);
  const toplamNakit = gelirGider.reduce((t, d) => t + (d.nakit || 0), 0);
  const toplamKredi = gelirGider.reduce((t, d) => t + (d.kart || 0), 0);
  const aktifMasa = masaDurumu.filter(m => m.durum === 'Dolu').length;
  const acikMasaTutar = siparisler
    .filter(s => s.odemeDurumu !== 'Ödendi' && s.odemeDurumu !== 'Odendi')
    .reduce((t, s) => t + (s.netTutar || s.toplamTutar || 0), 0);

  const trendDunCiro = dunOzet?.toplamCiro > 0 ? Math.round(((gunlukOzet?.toplamCiro || 0) - dunOzet.toplamCiro) / dunOzet.toplamCiro * 100) : null;

  const saatlikRange = activePeriod === 'custom' ? { start: startDate, end: endDate } : getDateRange(activePeriod);
  const saatlikVeri = (saatlikRange.start && saatlikRange.end && siparisler.length > 0) ? (() => {
    const filtered = siparisler.filter(s => {
      const d = toDateStr(new Date(s.siparisTarihi));
      return d >= saatlikRange.start && d <= saatlikRange.end;
    });
    const hourly = {};
    filtered.forEach(s => {
      const hour = new Date(s.siparisTarihi).getHours();
      if (!hourly[hour]) hourly[hour] = { saat: hour, adet: 0, tutar: 0 };
      hourly[hour].adet += 1;
      hourly[hour].tutar += (s.netTutar || s.toplamTutar || 0);
    });
    return Object.values(hourly).sort((a, b) => a.saat - b.saat).map(s => ({ ...s, label: `${String(s.saat).padStart(2, '0')}:00` }));
  })() : [];

  return (
    <div className="space-y-4">
      {/* Live Widgets */}
      <LiveWidgets acikMasa={aktifMasa} canliCiro={gunlukOzet?.toplamCiro || 0} doluMasaTutar={acikMasaTutar} />

      {/* Header + Filters */}
      <div className="bg-white dark:bg-surface-800 rounded-2xl border border-surface-100 dark:border-surface-700 shadow-soft p-4">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/20">
              <BarChart3 size={14} className="text-white" />
            </div>
            <h3 className="font-display font-bold text-surface-800 dark:text-white text-sm">Raporlar & Analitik</h3>
          </div>
          {loading && <div className="w-4 h-4 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />}
        </div>
        <Filters activePeriod={activePeriod} onChange={handlePeriodChange} startDate={startDate} endDate={endDate} onStartDateChange={setStartDate} onEndDateChange={setEndDate} />
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <KpiCard label="Ciro" value={fm(toplamCiro)} icon={<DollarSign size={16} />} trend={trendDunCiro} trendLabel="düne göre" sparklineData={gelirGider.map((d, i) => ({ name: i, value: d.toplam || 0 }))} color="emerald" />
        <KpiCard label="Nakit Tahsilat" value={fm(toplamNakit)} icon={<TrendingUp size={16} />} color="blue" />
        <KpiCard label="Kredi Kartı" value={fm(toplamKredi)} icon={<TrendingUp size={16} />} color="purple" />
        <KpiCard label="Açık Masa" value={aktifMasa} icon={<Users size={16} />} color="amber" />
        <KpiCard label="Açık Masa Tutarı" value={fm(acikMasaTutar)} icon={<Receipt size={16} />} color="orange" />
      </div>

      {/* Charts Row 1: Sales + Payment */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <SalesChart data={saatlikVeri} dataKey="tutar" xKey="label" title="Saatlik Satış Trendi" />
        </div>
        <div>
          <PaymentChart data={odemeDagilimi} />
        </div>
      </div>

      {/* Charts Row 2: Top Products + Category */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <TopProducts data={enCokSatilan} />
        <CategoryChart data={kategoriDagilimi} />
      </div>

      {/* Charts Row 3: Waiter + Table */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <WaiterStats data={garsonSatislari} />
        <TableAnalysis data={masaAnalizi} />
      </div>

      {/* Heatmap */}
      <Heatmap data={saatlikVeri} />
    </div>
  );
};

export default ReportsDashboard;
